import React, { useCallback, useMemo } from 'react'
import { NodeType } from 'shared/types/ingestion'
import { ListItem, NodeIndex } from './types'
import { Checkmark } from '../../../ingestion/IMSpanish/components/Checkmark'
import { ConfirmCommitButton } from 'shared/ui'
import { doInBatches } from 'shared/utils'
import { useAtomValue, useSetAtom } from 'infrastructure/state/jotai.wrapper'
import {
  idMapAtom,
  requiresUpdateListAtom,
  treeAtom,
  updatingAtom,
} from './atoms'
import { openNode } from '../../utility/TreeNavigation'
import { Button } from '@mui/material'
import { retrieveIds, updateTreeNode } from '../TreeRequests'
import { useResetForTree } from 'shared/hooks/trees/hooks'

type IncorrectCounts = {
  unit: number
  activity: number
  lesson_plan: number
  assessment: number
  presentation: number
}

export function incorrectCount(items: ListItem[]) {
  return items.reduce<IncorrectCounts>(
    (acc, item) => {
      if (!item.correctedIndex) return acc
      switch (item.node.type) {
        case NodeType.Unit:
          acc.unit++
          break
        case NodeType.Activity:
          acc.activity++
          break
        case NodeType.LessonPlan:
          acc.lesson_plan++
          break
        case NodeType.Assessment:
          acc.assessment++
          break
        case NodeType.Presentation:
          acc.presentation++
          break
        default:
          throw new Error(`Unexpected Type ${item.node.type}`)
      }
      return acc
    },
    {
      unit: 0,
      lesson_plan: 0,
      activity: 0,
      assessment: 0,
      presentation: 0,
    },
  )
}

export function countsTitle(counts: IncorrectCounts) {
  const { total, parts } = Object.entries(counts).reduce<{
    total: number
    parts: string[]
  }>(
    ({ total, parts }, [key, count]) => {
      return {
        total: total + count,
        parts: [...parts, `${count} ${key}s`],
      }
    },
    {
      total: 0,
      parts: [],
    },
  )
  return `${total} Need Updating: ${parts.join('; ')}`
}

export function useTableColumns() {
  const openListItem = useGoToListItem()
  const updateListItem = useUpdateListItems()
  const isUpdating = useAtomValue(updatingAtom)
  return useMemo(() => {
    return [
      {
        title: <strong>Title</strong>,
        width: 100,
        rowContent({ node: { title } }: ListItem) {
          return title
        },
      },
      {
        title: <strong>Type</strong>,
        width: 75,
        rowContent({ node: { type } }: ListItem) {
          return type
        },
      },
      {
        title: <strong>Current</strong>,
        width: 75,
        rowContent({ currentIndexes }: ListItem) {
          return currentIndexes
        },
      },
      {
        title: <strong>Correct</strong>,
        width: 25,
        rowContent({ correctedIndex }: ListItem) {
          return correctedIndex ?? <Checkmark />
        },
      },
      {
        title: <strong>Update</strong>,
        width: 75,
        rowContent(item: ListItem) {
          if (!item.correctedIndex) return null
          return (
            <ConfirmCommitButton
              disabled={isUpdating}
              onClick={() => updateListItem(item)}
            >
              Update
            </ConfirmCommitButton>
          )
        },
      },
      {
        title: <strong>Open</strong>,
        width: 75,
        rowContent(item: ListItem) {
          return <Button onClick={() => openListItem(item)}>New Tab</Button>
        },
      },
    ]
  }, [openListItem, updateListItem, isUpdating])
}

function useUpdateListItems() {
  const tree = useAtomValue(treeAtom)
  const resetTree = useResetForTree(tree)
  const setUpdating = useSetAtom(updatingAtom)
  return useCallback(
    async (...items: ListItem[]) => {
      setUpdating(true)
      const doOneUpdate = ({ node, correctedIndex }: ListItem) =>
        updateTreeNode({ ...retrieveIds(node), title: correctedIndex })
      await doInBatches(items, doOneUpdate)
      resetTree()
      setUpdating(false)
    },
    [resetTree, setUpdating],
  )
}

export function useUpdateAllItems() {
  const updateItems = useUpdateListItems()
  const needsUpdate = useAtomValue(requiresUpdateListAtom)
  return useCallback(() => {
    return updateItems(...needsUpdate)
  }, [needsUpdate, updateItems])
}

function useGoToListItem() {
  const idMap = useAtomValue(idMapAtom)
  return useCallback(
    ({ node }: ListItem) => {
      if (!idMap) return
      openNode(node, idMap)
    },
    [idMap],
  )
}

const typeTests: Record<
  string,
  {
    regex: RegExp
    shouldBe(r: RegExp, t: string, i: NodeIndex): string | undefined
  }[]
> = {
  [NodeType.Assessment]: [
    {
      regex: /\d\.\d{1,2}\.([A-Z])\d{1,2}/,
      shouldBe(
        regex: RegExp,
        title: string,
        { grade, unit, lesson_plan }: NodeIndex,
      ) {
        const match = regex.exec(title)
        if (!match) return
        const letterPart = match[1]
        const corrected = `${grade}.${unit}.${letterPart}${lesson_plan}`
        if (title.includes(corrected)) return
        return title.replace(regex, corrected)
      },
    },
    {
      regex: /Lesson \d{1,2}/,
      shouldBe(regex: RegExp, title: string, { lesson_plan }: NodeIndex) {
        const match = regex.test(title)
        if (!match) return
        const corrected = `Lesson ${lesson_plan}`
        if (title.includes(corrected)) return
        return title.replace(regex, corrected)
      },
    },
  ],
  [NodeType.Activity]: [
    {
      regex: /^\d{1,2}\.\d{1,2}/,
      shouldBe(
        regex: RegExp,
        title: string,
        { activity, lesson_plan }: NodeIndex,
      ) {
        const expected = `${lesson_plan}.${activity}`
        if (
          title.includes(`${expected}:`) ||
          (title.toLowerCase().includes('cool-down') &&
            title.includes(expected))
        )
          return
        return title.replace(regex, expected)
      },
    },
  ],
  [NodeType.LessonPlan]: [
    {
      regex: /Lesson \d\d?/,
      shouldBe(regex: RegExp, title: string, { lesson_plan }: NodeIndex) {
        const expected = `Lesson ${lesson_plan}`
        if (title.includes(`${expected}:`)) return
        return title.replace(regex, `Lesson ${lesson_plan}`)
      },
    },
  ],
  [NodeType.Unit]: [
    {
      regex: /Unit \d{1,2}/,
      shouldBe(regex: RegExp, title: string, { unit }: NodeIndex) {
        const expected = `Unit ${unit}`
        if (title.includes(`${expected}:`)) return
        return title.replace(regex, `Unit ${unit}`)
      },
    },
  ],
  [NodeType.Presentation]: [
    {
      regex: /Lesson \d\d?/,
      shouldBe(regex: RegExp, title: string, { lesson_plan }: NodeIndex) {
        const expected = `Lesson ${lesson_plan}`
        if (title.includes(`${expected}:`)) return
        return title.replace(regex, `Lesson ${lesson_plan}`)
      },
    },
  ],
}

export function nodeTitleTest(node: { type: string; title: string }) {
  return typeTests[node.type]?.find(({ regex }) => regex.test(node.title))
}

export function getCorrectedTitle(
  node: { type: string; title: string },
  indexes: NodeIndex,
) {
  const test = nodeTitleTest(node)
  return test?.shouldBe(test.regex, node.title, indexes)
}
